import {isFunction, isObject} from 'lodash';
import {FC, useCallback, useEffect, useState} from 'react';

import {isInteger} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {useLayer} from '../../../../libs/shared/ui/src/components/layers/layerContext';
import {buildTeammateDisplayName} from '../../../helpers/modelHelpers';
import {HelpChatConfigModel} from '../../../models/api/helpChatConfigModel';
import {TeammateModel} from '../../../models/api/teammateModel';

/*
 * Props.
 */

interface HelpChatProps {
  user: TeammateModel;
  helpChatConfig: HelpChatConfigModel;
  helpChatOpenAttempt: number;
}

/*
 * Component.
 */

export const HelpChat: FC<HelpChatProps> = (props) => {
  const {user, helpChatConfig, helpChatOpenAttempt} = props;
  const [chatWidget, setChatWidget] = useState<(...args: any[]) => void>();
  const {window} = useLayer();

  /*
   * Manage the widget's lifecycle.
   */

  const onScriptLoad = useCallback(() => {
    // The "FrontChat" function changes over time.
    // As a result, we need to call it on the window every time.
    const newChatWidget = (...args: Array<any>): unknown => {
      if (!('FrontChat' in window) || !isFunction(window.FrontChat)) {
        return undefined;
      }

      return window.FrontChat(...args);
    };

    setChatWidget(() => newChatWidget);
  }, [window]);

  useEffect(() => {
    const scriptDocument = window.document;
    const script = scriptDocument.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://chat-assets.frontapp.com/v1/chat.bundle.js';
    script.async = true;
    script.onload = onScriptLoad;

    scriptDocument.body.appendChild(script);
    return () => {
      scriptDocument.body.removeChild(script);
    };
  }, [window, onScriptLoad]);

  /*
   * Control the widget.
   */

  const {chatId, userHash} = helpChatConfig;
  const userEmail = user.email;
  const userDisplayName = buildTeammateDisplayName(user);

  const openHelpChat = useCallback(() => {
    if (!chatWidget) {
      return;
    }

    chatWidget('show');
  }, [chatWidget]);

  // Update the widget configuration when it changes.
  useEffect(() => {
    if (!chatWidget) {
      return undefined;
    }

    chatWidget('init', {
      useDefaultLauncher: false,
      chatId,
      userHash,
      email: userEmail,
      name: userDisplayName,
    });

    return () => {
      chatWidget('shutdown');
    };
  }, [chatWidget, chatId, userHash, userEmail, userDisplayName]);

  // Register for new message notifications on the widget.
  useEffect(() => {
    if (!chatWidget) {
      return undefined;
    }

    // When the number of unread messages changes, we may need to open the chat.
    const onUnreadChange = (options: unknown) => {
      if (!isObject(options) || !('unread_count' in options) || !isInteger(options.unread_count)) {
        return;
      }

      if (options.unread_count === 0) {
        return;
      }

      openHelpChat();
    };

    chatWidget('onUnreadChange', onUnreadChange);
    return () => {
      chatWidget('offUnreadChange', onUnreadChange);
    };
  }, [chatWidget, openHelpChat]);

  // Open the widget when requested.
  useEffect(() => {
    if (helpChatOpenAttempt === 0) {
      return;
    }

    openHelpChat();
  }, [helpChatOpenAttempt, openHelpChat]);

  /*
   * Render.
   */

  return null;
};
